import { action, computed, makeObservable, observable } from 'mobx';

import ApiService from '../services/ApiService';

import { GaPageLabel } from '../services/AnalyticsService';
import { IModalStore, ModalType } from './ModalStore';
import { IInvoiceDetailsRequest } from './models/IInvoiceDetailsRequest';
import { InvoiceDetailsResponse } from './models/InvoiceDetailsResponse';
import { Loadable } from './models/Loadable';
import { IRouteStore } from './RouteStore';
import { ITranslateStore } from './TranslateStore';
import { Line } from './models/InvoiceDetails';
import UnsettledReceivablesStore from './UnsettledReceivablesStore';
import PreviousInvoicesStore from './PreviousInvoicesStore';
import LayoutStore from './LayoutStore';
import { Invoice } from './models/Invoice';

type IndentLevel = 0 | 1 | 2 | 3;
type TableColumnWidth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export default class InvoiceDetailsStore {
  @observable
  public invoiceDetailsLoading = false;
  @observable
  public activeLineUsages: Line = null;
  @observable
  public activeLineUsagesId: string = null;
  @observable
  public openLines: string[] = [];
  @observable
  public lastOpenedLine: string = null;
  @observable
  private allInvoiceDetails: Map<string, Loadable<InvoiceDetailsResponse>> = new Map();
  @observable
  private activeInvoiceDetailsRequest: IInvoiceDetailsRequest = null;
  @observable
  private activeInvoiceNumber: string = null;

  constructor(
    private apiService: ApiService,
    private translateStore: ITranslateStore,
    private routeStore: IRouteStore,
    private modalStore: IModalStore,
    private unsettledReceivablesStore: UnsettledReceivablesStore,
    private previousInvoicesStore: PreviousInvoicesStore,
    private layoutStore: LayoutStore
  ) {
    makeObservable(this);
  }

  @action
  public setActiveLineUsages(line: Line): void {
    this.activeLineUsages = line;
  }

  @action
  public setActiveLineUsagesId(id: string): void {
    this.activeLineUsagesId = id;
  }

  @computed
  public get invoiceDetails(): Loadable<InvoiceDetailsResponse> {
    return this.allInvoiceDetails.get(this.currentInvoiceNumber);
  }

  public async activateInvoiceDetails(invoice: IInvoiceDetailsRequest, gaPageLabel: string): Promise<any> {
    this.closeAllLinesAndClearLastOpenedLine();

    this.setActiveInvoiceDetailsRequest(invoice);

    this.modalStore.openModal(ModalType.InvoiceDetailsModal, gaPageLabel);

    await this.loadInvoiceDetails(invoice);
    await this.previousInvoicesStore.tryLoadPreviousInvoices();

    this.openFirstLineWhenOneLineOnly();
  }

  @action
  public async activateInvoiceDetailsByInvoiceNumber(invoiceNumber: string): Promise<any> {
    this.setInvoiceDetailsLoading(true);

    this.setActiveInvoiceNumber(invoiceNumber);

    this.modalStore.openModal(ModalType.InvoiceDetailsModal);

    await this.previousInvoicesStore.tryLoadPreviousInvoices();
    await this.unsettledReceivablesStore.tryLoadUnsettledReceivables();
    const invoice = this.findInvoiceByNumber(this.activeInvoiceNumber);

    if (invoice != null) {
      this.setActiveInvoiceDetailsRequest(invoice);
      await this.loadInvoiceDetails(invoice);
      this.openFirstLineWhenOneLineOnly();
    }

    this.setInvoiceDetailsLoading(false);
  }

  public formatAsQuantity(quantity: number): string {
    const quantityUnit = this.translateStore.translate('shared.invoice-details-modal.quantity-unit');
    return quantity != null ? `${quantity} ${quantityUnit}` : null;
  }

  @computed
  public get invoiceDetailsRequest(): IInvoiceDetailsRequest {
    return this.activeInvoiceDetailsRequest;
  }

  @computed
  public get invoiceDetailsRequestLoading(): boolean {
    if (this.activeInvoiceDetailsRequest != null) {
      return false;
    }
    return this.previousInvoicesStore.previousInvoices.isInitialLoading || this.unsettledReceivablesStore.unsettledReceivables.isInitialLoading;
  }

  @computed
  public get invoiceNotFound(): boolean {
    return this.invoiceDetailsRequest == null;
  }

  @computed
  public get sourceHasUsages(): boolean {
    return this.invoiceDetailsRequest.source !== 'DAX';
  }

  @computed
  public get currentInvoiceNumber(): string {
    return this.invoiceDetailsRequest.invoiceNumber;
  }

  @computed
  public get isCurrentInvoiceRestricted(): boolean {
    return this.invoiceDetailsRequest.subscriber != null;
  }

  @computed
  public get notAllowedToShowUsagesButCanBeAllowed(): boolean {
    return this.sourceHasUsages && !this.invoiceDetailsRequest.invoiceServiceDetailsAccess && !this.isCurrentInvoiceRestricted;
  }

  @action
  public setOpenLines(openItems: string[]): void {
    if (this.layoutStore.isMobile) {
      this.openLines = openItems.filter((openItem) => openItem.search('usages$') === -1);
    } else {
      this.openLines = openItems;
    }
  }

  @action
  public setLastOpenedLine(lineId: string): void {
    this.lastOpenedLine = lineId;
  }

  public tableColumnWidthByColumnIndex(index: number): TableColumnWidth {
    switch (index) {
      case 0:
        return 6;
      case 1:
      case 2:
      case 3:
        return 2;
      default:
        throw new Error();
    }
  }

  public getIndentLevel(lineIndex: number[]): IndentLevel {
    const indentLevel = lineIndex.length - 1;
    if (indentLevel > 3) {
      return 3;
    }
    return indentLevel as IndentLevel;
  }

  public tableLastColumnGridColumnWidthByColumnIndex(index: number): any[] {
    const widths = [
      ['xs-8', 'sm-9', 'md-8', 'lg-9'],
      ['xs-3', 'sm-2', 'md-3', 'lg-2']
    ];
    return widths[index];
  }

  @computed
  public get gaPageLabel(): GaPageLabel {
    return this.modalStore.gaPageLabel;
  }

  @action
  private setInvoiceDetailsLoading(isLoading: boolean): void {
    this.invoiceDetailsLoading = isLoading;
  }

  private openFirstLineWhenOneLineOnly() {
    if (
      this.invoiceDetails.payload.status === 'OK' &&
      this.invoiceDetails.payload.details.lineGroups &&
      this.invoiceDetails.payload.details.lineGroups.length === 1
    ) {
      this.setOpenLines(['details-row-0']);
      this.setLastOpenedLine('details-row-0');
    }
  }

  private closeAllLinesAndClearLastOpenedLine() {
    this.setOpenLines([]);
    this.setLastOpenedLine(null);
  }

  public findInvoiceByNumber(requestedInvoiceNumber: string): Invoice {
    for (const invoice of this.unsettledReceivablesStore.unsettledReceivables.payload.unpaidInvoices) {
      if (invoice.invoiceNumber === requestedInvoiceNumber) {
        return invoice;
      }
    }
    if (this.previousInvoicesStore.previousInvoices?.payload) {
      for (const invoice of this.previousInvoicesStore.previousInvoices.payload) {
        if (invoice.invoiceNumber === requestedInvoiceNumber) {
          return invoice;
        }
      }
    }
    return null;
  }

  private async loadInvoiceDetails(invoice: IInvoiceDetailsRequest) {
    if (!this.allInvoiceDetails.has(invoice.invoiceNumber)) {
      const promise = this.apiService.getInvoiceDetails(invoice);
      const invoiceDetailsResponse: Loadable<InvoiceDetailsResponse> = Loadable.toLoadable(promise);
      this.setInvoiceDetails(invoice.invoiceNumber, invoiceDetailsResponse);
      await promise;
    }
  }

  @action
  private setInvoiceDetails(invoiceNumber: string, newInvoiceDetails: Loadable<InvoiceDetailsResponse>): void {
    this.allInvoiceDetails.set(invoiceNumber, newInvoiceDetails);
  }

  @action
  private setActiveInvoiceDetailsRequest(invoiceDetailsRequest: IInvoiceDetailsRequest): void {
    this.activeInvoiceDetailsRequest = invoiceDetailsRequest;
  }

  @action
  private setActiveInvoiceNumber(invoiceNumber: string): void {
    this.activeInvoiceNumber = invoiceNumber;
  }
}
